import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { storeAPI, purchasesAPI, tasksAPI } from '../services/api';
import Modal from '../components/Modal/Modal';
import { 
  Settings, 
  Plus, 
  Edit, 
  Trash2, 
  ToggleLeft, 
  ToggleRight,
  BarChart3,
  Package,
  ShoppingBag,
  X,
  Save,
  RotateCcw
} from 'lucide-react';
import { toast } from 'react-toastify';

const Admin = () => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('items'); // items, purchases, stats
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  
  const [newItem, setNewItem] = useState({
    name: '',
    description: '',
    pookiePointsCost: 50,
    category: 'other',
    emoji: '🎁',
    stock: -1,
    isSpecial: false,
    specialMessage: '',
    validUntil: '',
  });

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchAdminData();
    }
  }, [user]);

  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const fetchAdminData = async () => {
    try {
      setLoading(true);
      const [itemsResponse, purchasesResponse, statsResponse] = await Promise.all([
        storeAPI.getAdminItems(),
        purchasesAPI.getAdminPurchases(),
        purchasesAPI.getStats(),
      ]);

      if (itemsResponse.data.success) {
        setItems(itemsResponse.data.items);
      }

      if (purchasesResponse.data.success) {
        setPurchases(purchasesResponse.data.purchases);
      }

      if (statsResponse.data.success) {
        setStats(statsResponse.data.stats);
      }
    } catch (error) {
      toast.error('Error loading admin data! 😢');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateItem = async (e) => {
    e.preventDefault();
    try {
      const response = await storeAPI.createItem(newItem);
      if (response.data.success) {
        setItems([response.data.item, ...items]);
        setNewItem({
          name: '',
          description: '',
          pookiePointsCost: 50,
          category: 'other',
          emoji: '🎁',
          stock: -1,
          isSpecial: false,
          specialMessage: '',
          validUntil: '',
        });
        setShowCreateModal(false);
        toast.success(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error creating item! 😢');
    }
  };

  const handleUpdateItem = async (e) => {
    e.preventDefault();
    try {
      const response = await storeAPI.updateItem(editingItem._id, editingItem);
      if (response.data.success) {
        setItems(items.map(item => 
          item._id === editingItem._id ? response.data.item : item
        ));
        setEditingItem(null);
        toast.success(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error updating item! 😢');
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (window.confirm('Are you sure you want to delete this item? 🗑️')) {
      try {
        const response = await storeAPI.deleteItem(itemId);
        if (response.data.success) {
          setItems(items.filter(item => item._id !== itemId));
          toast.success(response.data.message);
        }
      } catch (error) {
        toast.error(error.response?.data?.message || 'Error deleting item! 😢');
      }
    }
  };

  const handleToggleItem = async (itemId) => {
    try {
      const response = await storeAPI.toggleItem(itemId);
      if (response.data.success) {
        setItems(items.map(item => 
          item._id === itemId ? response.data.item : item
        ));
        toast.success(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error toggling item! 😢');
    }
  };

  const handleFulfillPurchase = async (purchaseId) => {
    const notes = prompt('Add any notes (optional):');
    try {
      const response = await purchasesAPI.fulfillPurchase(purchaseId, notes);
      if (response.data.success) {
        setPurchases(purchases.map(purchase => 
          purchase._id === purchaseId ? response.data.purchase : purchase
        ));
        toast.success(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error fulfilling purchase! 😢');
    }
  };

  const handleCancelPurchase = async (purchaseId) => {
    try {
      const response = await purchasesAPI.cancelPurchase(purchaseId);
      if (response.data.success) {
        toast.success(response.data.message);
        fetchAdminData();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error cancelling purchase! 😢');
    }
  };

  const handleForceReset = async () => {
    try {
      const confirmReset = window.confirm('Are you sure you want to force reset all recurring tasks? This will reset ALL completed recurring tasks and clean up old non-recurring tasks.');
      
      if (!confirmReset) return;
      
      const response = await tasksAPI.forceReset();
      if (response.data.success) {
        toast.success(response.data.message);
        console.log('Reset Summary:', response.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error resetting tasks! 😢');
    }
  };

  const ItemCard = ({ item }) => (
    <div className="card-cute">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <span className="text-2xl">{item.emoji}</span>
          <div>
            <h3 className="font-semibold">{item.name}</h3>
            <p className="text-sm text-gray-600">{item.description}</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => handleToggleItem(item._id)}
            className={`p-1 rounded ${item.isAvailable ? 'text-green-600' : 'text-gray-400'}`}
            title={item.isAvailable ? 'Active' : 'Inactive'}
          >
            {item.isAvailable ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
          </button>
          <button
            onClick={() => setEditingItem(item)}
            className="p-1 text-pookie-blue-600 hover:text-pookie-blue-700"
            title="Edit"
          >
            <Edit className="w-4 h-4" />
          </button>
          <button
            onClick={() => handleDeleteItem(item._id)}
            className="p-1 text-red-600 hover:text-red-700"
            title="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="flex items-center justify-between text-sm">
        <div className="flex items-center space-x-4">
          <div className="points-display text-xs">
            💎 {item.pookiePointsCost}
          </div>
          <span className={`px-2 py-1 rounded-full text-xs ${
            item.category === 'date' ? 'bg-pink-100 text-pink-600' :
            item.category === 'treat' ? 'bg-yellow-100 text-yellow-600' :
            item.category === 'favor' ? 'bg-blue-100 text-blue-600' :
            item.category === 'surprise' ? 'bg-purple-100 text-purple-600' :
            'bg-gray-100 text-gray-600'
          }`}>
            {item.category}
          </span>
        </div>
        <span className="text-xs text-gray-500">
          {item.stock === -1 ? 'Unlimited' : `${item.stock} left`}
        </span>
      </div>
    </div>
  );

  const PurchaseCard = ({ purchase }) => (
    <div className="card-cute">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center space-x-3">
          <span className="text-2xl">{purchase.item?.emoji || '🎁'}</span>
          <div>
            <h3 className="font-semibold">{purchase.itemName}</h3>
            <p className="text-sm text-gray-600">
              Purchased by {purchase.user?.displayName}
            </p>
          </div>
        </div>
        <div className="text-right">
          <div className="points-display text-sm">
            💎 {purchase.pookiePointsSpent}
          </div>
          <div className={`text-xs mt-1 px-2 py-1 rounded-full ${
            purchase.status === 'fulfilled' 
              ? 'bg-green-100 text-green-600' 
              : purchase.status === 'pending'
              ? 'bg-yellow-100 text-yellow-600'
              : 'bg-red-100 text-red-600'
          }`}>
            {purchase.status}
          </div>
        </div>
      </div>

      {purchase.status === 'pending' && (
        <div className="flex space-x-2 mt-3">
          <button
            onClick={() => handleFulfillPurchase(purchase._id)}
            className="btn-success text-xs px-3 py-1 flex-1"
          >
            Fulfill
          </button>
          <button
            onClick={() => handleCancelPurchase(purchase._id)}
            className="btn-danger text-xs px-3 py-1 flex-1"
          >
            Cancel
          </button>
        </div>
      )}

      <div className="mt-2 text-xs text-gray-500">
        {new Date(purchase.createdAt).toLocaleDateString()}
      </div>
    </div>
  );

  const StatsCard = ({ title, value, icon: Icon, color }) => (
    <div className={`card-cute ${color} p-4`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-600">{title}</p>
          <p className="text-2xl font-bold">{value}</p>
        </div>
        <Icon className="w-8 h-8 text-gray-600" />
      </div>
    </div>
  );

  if (user?.role !== 'admin') {
    return (
      <div className="text-center py-12">
        <Settings className="w-16 h-16 mx-auto mb-4 text-gray-400" />
        <h2 className="text-xl font-semibold text-gray-600 mb-2">Admin Access Required</h2>
        <p className="text-gray-500">You need admin privileges to access this page! 👑</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-pookie-pink-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gradient">Admin Dashboard</h1>
          <p className="text-gray-600">Manage your Pookie Store! 👑</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={handleForceReset}
            className="btn-danger flex items-center space-x-2"
            title="Force reset all recurring tasks (Admin only)"
          >
            <RotateCcw className="w-4 h-4" />
            <span>Force Reset Tasks</span>
          </button>
          {activeTab === 'items' && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn-primary flex items-center space-x-2"
            >
              <Plus className="w-4 h-4" />
              <span>Add Item</span>
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex space-x-1 bg-white p-1 rounded-lg shadow-sm">
        {[
          { key: 'items', label: 'Store Items', icon: ShoppingBag },
          { key: 'purchases', label: 'Purchases', icon: Package },
          { key: 'stats', label: 'Statistics', icon: BarChart3 },
        ].map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all ${
              activeTab === key
                ? 'bg-pookie-pink-500 text-white'
                : 'text-gray-600 hover:bg-pookie-pink-50'
            }`}
          >
            <Icon className="w-4 h-4" />
            <span>{label}</span>
          </button>
        ))}
      </div>

      {/* Items Tab */}
      {activeTab === 'items' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((item) => (
            <ItemCard key={item._id} item={item} />
          ))}
        </div>
      )}

      {/* Purchases Tab */}
      {activeTab === 'purchases' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {purchases.map((purchase) => (
            <PurchaseCard key={purchase._id} purchase={purchase} />
          ))}
        </div>
      )}

      {/* Stats Tab */}
      {activeTab === 'stats' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <StatsCard
              title="Total Purchases"
              value={stats.totalPurchases || 0}
              icon={Package}
              color="bg-blue-50"
            />
            <StatsCard
              title="Pending"
              value={stats.pendingPurchases || 0}
              icon={Package}
              color="bg-yellow-50"
            />
            <StatsCard
              title="Fulfilled"
              value={stats.fulfilledPurchases || 0}
              icon={Package}
              color="bg-green-50"
            />
            <StatsCard
              title="Points Spent"
              value={stats.totalPointsSpent || 0}
              icon={BarChart3}
              color="bg-purple-50"
            />
          </div>

          {/* User Stats */}
          {stats.userStats && stats.userStats.length > 0 && (
            <div className="card">
              <h3 className="text-lg font-semibold mb-4">User Statistics</h3>
              <div className="space-y-3">
                {stats.userStats.map((userStat) => (
                  <div key={userStat._id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <span className="text-lg">{userStat.avatar}</span>
                      <span className="font-medium">{userStat.displayName}</span>
                    </div>
                    <div className="text-right">
                      <div className="points-display text-sm">
                        💎 {userStat.totalSpent}
                      </div>
                      <div className="text-xs text-gray-500">
                        {userStat.purchaseCount} purchases
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Create Item Modal */}
      {showCreateModal && (
        <Modal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          title="Add Store Item"
          actions={
            <div className="flex space-x-3">
              <button
                type="submit"
                form="create-item-form"
                className="btn-primary flex-1"
              >
                Add Item
              </button>
              <button
                type="button"
                onClick={() => setShowCreateModal(false)}
                className="btn-secondary flex-1"
              >
                Cancel
              </button>
            </div>
          }
        >
          <form id="create-item-form" onSubmit={handleCreateItem} className="space-y-4">
            <div>
              <label className="label-cute">Item Name</label>
              <input
                type="text"
                value={newItem.name}
                onChange={(e) => setNewItem({...newItem, name: e.target.value})}
                className="input-cute"
                placeholder="Cute item name"
                required
              />
            </div>

            <div>
              <label className="label-cute">Description</label>
              <textarea
                value={newItem.description}
                onChange={(e) => setNewItem({...newItem, description: e.target.value})}
                className="input-cute"
                placeholder="What makes this special?"
                rows={3}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label-cute">Pookie Points Cost</label>
                <input
                  type="number"
                  value={newItem.pookiePointsCost}
                  onChange={(e) => setNewItem({...newItem, pookiePointsCost: parseInt(e.target.value)})}
                  className="input-cute"
                  min="1"
                  required
                />
              </div>

              <div>
                <label className="label-cute">Emoji</label>
                <input
                  type="text"
                  value={newItem.emoji}
                  onChange={(e) => setNewItem({...newItem, emoji: e.target.value})}
                  className="input-cute"
                  placeholder="🎁"
                  maxLength="2"
                />
              </div>
            </div>

            <div>
              <label className="label-cute">Category</label>
              <select
                value={newItem.category}
                onChange={(e) => setNewItem({...newItem, category: e.target.value})}
                className="input-cute"
              >
                <option value="date">Date</option>
                <option value="treat">Treat</option>
                <option value="favor">Favor</option>
                <option value="surprise">Surprise</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <label className="label-cute">Stock (-1 for unlimited)</label>
              <input
                type="number"
                value={newItem.stock}
                onChange={(e) => setNewItem({...newItem, stock: parseInt(e.target.value)})}
                className="input-cute"
                min="-1"
              />
            </div>

            <div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={newItem.isSpecial}
                  onChange={(e) => setNewItem({...newItem, isSpecial: e.target.checked})}
                  className="checkbox-cute"
                />
                <span className="label-cute mb-0">Special Item</span>
              </label>
            </div>

            {newItem.isSpecial && (
              <div>
                <label className="label-cute">Special Message</label>
                <input
                  type="text"
                  value={newItem.specialMessage}
                  onChange={(e) => setNewItem({...newItem, specialMessage: e.target.value})}
                  className="input-cute"
                  placeholder="Why is this special?"
                />
              </div>
            )}

            <div>
              <label className="label-cute">Valid Until (Optional)</label>
              <input
                type="date"
                value={newItem.validUntil}
                onChange={(e) => setNewItem({...newItem, validUntil: e.target.value})}
                className="input-cute"
              />
            </div>
          </form>
        </Modal>
      )}

      {/* Edit Item Modal */}
      {editingItem && (
        <Modal
          isOpen={!!editingItem}
          onClose={() => setEditingItem(null)}
          title="Edit Item"
          actions={
            <div className="flex space-x-3">
              <button
                type="submit"
                form="edit-item-form"
                className="btn-primary flex-1 flex items-center justify-center space-x-2"
              >
                <Save className="w-4 h-4" />
                <span>Save Changes</span>
              </button>
              <button
                type="button"
                onClick={() => setEditingItem(null)}
                className="btn-secondary flex-1"
              >
                Cancel
              </button>
            </div>
          }
        >
          <form id="edit-item-form" onSubmit={handleUpdateItem} className="space-y-4">
            <div>
              <label className="label-cute">Item Name</label>
              <input
                type="text"
                value={editingItem.name}
                onChange={(e) => setEditingItem({...editingItem, name: e.target.value})}
                className="input-cute"
                required
              />
            </div>

            <div>
              <label className="label-cute">Description</label>
              <textarea
                value={editingItem.description}
                onChange={(e) => setEditingItem({...editingItem, description: e.target.value})}
                className="input-cute"
                rows={3}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label-cute">Pookie Points Cost</label>
                <input
                  type="number"
                  value={editingItem.pookiePointsCost}
                  onChange={(e) => setEditingItem({...editingItem, pookiePointsCost: parseInt(e.target.value)})}
                  className="input-cute"
                  min="1"
                  required
                />
              </div>

              <div>
                <label className="label-cute">Emoji</label>
                <input
                  type="text"
                  value={editingItem.emoji}
                  onChange={(e) => setEditingItem({...editingItem, emoji: e.target.value})}
                  className="input-cute"
                  placeholder="🎁"
                  maxLength="2"
                />
              </div>
            </div>

            <div>
              <label className="label-cute">Category</label>
              <select
                value={editingItem.category}
                onChange={(e) => setEditingItem({...editingItem, category: e.target.value})}
                className="input-cute"
              >
                <option value="date">Date</option>
                <option value="treat">Treat</option>
                <option value="favor">Favor</option>
                <option value="surprise">Surprise</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <label className="label-cute">Stock (-1 for unlimited)</label>
              <input
                type="number"
                value={editingItem.stock}
                onChange={(e) => setEditingItem({...editingItem, stock: parseInt(e.target.value)})}
                className="input-cute"
                min="-1"
              />
            </div>

            <div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={editingItem.isSpecial}
                  onChange={(e) => setEditingItem({...editingItem, isSpecial: e.target.checked})}
                  className="checkbox-cute"
                />
                <span className="label-cute mb-0">Special Item</span>
              </label>
            </div>

            {editingItem.isSpecial && (
              <div>
                <label className="label-cute">Special Message</label>
                <input
                  type="text"
                  value={editingItem.specialMessage}
                  onChange={(e) => setEditingItem({...editingItem, specialMessage: e.target.value})}
                  className="input-cute"
                  placeholder="Why is this special?"
                />
              </div>
            )}

            <div>
              <label className="label-cute">Valid Until (Optional)</label>
              <input
                type="date"
                value={editingItem.validUntil ? editingItem.validUntil.split('T')[0] : ''}
                onChange={(e) => setEditingItem({...editingItem, validUntil: e.target.value})}
                className="input-cute"
              />
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
};

export default Admin; 